/**
 * Safe message formatter for debate messages
 * Supports basic markdown (bold, italic) while stripping dangerous content
 */

// URL detection regex - matches http/https URLs and common URL patterns
const URL_REGEX = /https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.[a-z]{2,}\/[^^\s]*/gi;

// Conservative domain whitelist for clickable links
export const WHITELISTED_DOMAINS: string[] = [
  // Knowledge and reference
  'wikipedia.org', 'en.wikipedia.org', 'archive.org', 'web.archive.org',
  // Newswires and major outlets
  'reuters.com', 'apnews.com', 'bbc.com', 'bbc.co.uk', 'theguardian.com', 'nytimes.com', 'washingtonpost.com', 'economist.com', 'wsj.com', 'npr.org',
  'cnn.com', 'cbsnews.com', 'nbcnews.com', 'abcnews.go.com', 'foxnews.com', 'usatoday.com', 'latimes.com', 'time.com', 'theatlantic.com', 'newyorker.com',
  // Science and research
  'arxiv.org', 'doi.org', 'nature.com', 'science.org', 'sciencedirect.com', 'springer.com', 'jamanetwork.com', 'thelancet.com', 'bmj.com', 'ncbi.nlm.nih.gov',
  'biorxiv.org', 'medrxiv.org', 'ssrn.com', 'osf.io', 'zenodo.org', 'wiley.com', 'tandfonline.com', 'oup.com', 'cambridge.org', 'sagepub.com', 'sciencemag.org', 'pnas.org', 'cell.com', 'nejm.org',
  // Government and public records
  'whitehouse.gov', 'congress.gov', 'supremecourt.gov', 'sec.gov', 'federalregister.gov', 'justice.gov', 'govinfo.gov', 'nih.gov', 'cdc.gov', 'epa.gov', 'data.gov', 'census.gov',
  // International orgs
  'who.int', 'un.org', 'europa.eu', 'imf.org', 'worldbank.org', 'oecd.org', 'wto.org', 'unicef.org', 'unesco.org', 'undp.org',
  // Document repositories used by journalists
  'documentcloud.org', 'courtlistener.com', 'propublica.org', 'icij.org', 'cochranelibrary.com', 'ourworldindata.org', 'perma.cc',
  // Trusted self-declared domain
  'whn.global',
  // Additional major outlets
  'bloomberg.com', 'ft.com', 'pbs.org', 'aljazeera.com', 'politico.com', 'axios.com',
  // UK and Europe
  'thetimes.co.uk', 'telegraph.co.uk', 'independent.co.uk', 'sky.com',
  'spiegel.de', 'zeit.de', 'faz.net', 'sueddeutsche.de',
  'lemonde.fr', 'lefigaro.fr', 'liberation.fr',
  'elpais.com', 'elmundo.es',
  'repubblica.it', 'corriere.it',
  // Canada & Australia
  'cbc.ca', 'theglobeandmail.com', 'globeandmail.com', 'nationalpost.com',
  'abc.net.au', 'smh.com.au', 'theage.com.au',
  // Japan & Israel
  'japantimes.co.jp', 'nhk.or.jp', 'haaretz.com', 'timesofisrael.com',
  // Middle East
  'arabnews.com', 'thenationalnews.com',
  // Fact-checkers
  'snopes.com', 'politifact.com', 'factcheck.org', 'fullfact.org',
  // Tabloids (allowed for safety; credibility is judged by users/AI prompts)
  'dailymail.co.uk', 'thesun.co.uk', 'mirror.co.uk', 'nypost.com'
];

// Public-suffix style whitelist (match any subdomain ending with these)
export const WHITELISTED_SUFFIXES: string[] = [
  '.gov', '.edu', '.europa.eu', '.un.org', '.who.int', '.gov.uk',
  // Additional academic/education ccTLDs
  '.ac.uk', '.edu.au', '.ac.jp'
];

// Simple markdown patterns
const MARKDOWN_PATTERNS = {
  bold: /\*\*(.*?)\*\*/g,
  italic: /\*(.*?)\*/g,
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeUrl(candidate: string): URL | null {
  const trimmed = candidate.trim();
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const u = new URL(trimmed);
      return (u.protocol === 'http:' || u.protocol === 'https:') ? u : null;
    }
    // Add http scheme if missing for parsing
    const u = new URL(`http://${trimmed}`);
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u : null;
  } catch {
    return null;
  }
}

function isWhitelistedHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (WHITELISTED_DOMAINS.some(d => host === d || host.endsWith(`.${d}`))) return true;
  if (WHITELISTED_SUFFIXES.some(s => host === s.slice(1) || host.endsWith(s))) return true;
  return false;
}

function buildSafeAnchor(url: URL, text: string): string {
  const safeHref = `${url.protocol}//${url.host}${url.pathname}${url.search}`;
  const safeText = escapeHtml(text || safeHref);
  return `<a href="${safeHref}" target="_blank" rel="nofollow ugc noopener noreferrer">${safeText}</a>`;
}

function renderLinksWithWhitelist(text: string, unverifiedNote: string): string {
  return text.replace(URL_REGEX, (match) => {
    const url = normalizeUrl(match);
    if (!url || !isWhitelistedHost(url.hostname)) {
      return `${escapeHtml(match)}${escapeHtml(unverifiedNote)}`;
    }
    return buildSafeAnchor(url, match);
  });
}

/**
 * Converts simple markdown to HTML
 */
function parseMarkdown(text: string): string {
  let formatted = text;
  
  // Convert **bold** to <strong>
  formatted = formatted.replace(MARKDOWN_PATTERNS.bold, '<strong>$1</strong>');
  
  // Convert *italic* to <em> (but not if it's part of **bold**)
  formatted = formatted.replace(MARKDOWN_PATTERNS.italic, '<em>$1</em>');
  
  return formatted;
}

/**
 * Sanitizes text by removing potentially dangerous content
 */
function sanitizeText(text: string, unverifiedNote: string): string {
  // Remove script tags and other dangerous HTML
  const dangerous = /<script[^>]*>.*?<\/script>/gi;
  let sanitized = text.replace(dangerous, '');

  // Sanitize and whitelist only safe anchors
  const anchorRegex = /<a\b[^>]*href=["']?([^"'>\s]+)["']?[^>]*>(.*?)<\/a>/gi;
  sanitized = sanitized.replace(anchorRegex, (_m, href: string, inner: string) => {
    const url = normalizeUrl(href);
    if (url && isWhitelistedHost(url.hostname)) {
      return buildSafeAnchor(url, inner);
    }
    const display = url ? `${url.protocol}//${url.host}${url.pathname}${url.search}` : href;
    return `${escapeHtml(display)}${escapeHtml(unverifiedNote)}`;
  });

  // Remove any HTML tags except our allowed ones
  const allowedTags = ['strong', 'em', 'a'];
  const htmlTagRegex = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;

  sanitized = sanitized.replace(htmlTagRegex, (match, tagName) => {
    if (allowedTags.includes(tagName.toLowerCase())) {
      return match;
    }
    return ''; // Remove disallowed tags
  });

  return sanitized;
}

export type FormatMessageOptions = {
  /** Appended to non-whitelisted URLs (should be translated in the UI layer). */
  unverifiedLinkNote?: string;
};

const DEFAULT_UNVERIFIED_NOTE = ' (not reviewed)';

/**
 * Inserts clipboard plain text only (like pasting into Notepad). Returns null to use the browser default.
 */
export function handlePlainTextPaste(event: ClipboardEvent): string | null {
  if (!event.clipboardData) return null;
  const plain = event.clipboardData.getData('text/plain');
  if (plain == null || plain === '') return null;
  return plain;
}

/**
 * Main function to format message content safely
 */
export function formatMessageContent(content: string, options?: FormatMessageOptions): string {
  if (!content) return '';

  const unverifiedNote = options?.unverifiedLinkNote ?? DEFAULT_UNVERIFIED_NOTE;
  
  // Step 1: If there is no existing anchor markup, convert bare URLs to whitelisted anchors
  // Avoid double-wrapping and breaking existing <a> tags
  const hasAnchorMarkup = /<a\b/i.test(content);
  let formatted = hasAnchorMarkup ? content : renderLinksWithWhitelist(content, unverifiedNote);
  
  // Step 2: Parse simple markdown
  formatted = parseMarkdown(formatted);
  
  // Step 3: Sanitize the result
  formatted = sanitizeText(formatted, unverifiedNote);
  
  return formatted;
}

/**
 * Gets the plain text version of a message (for notifications, etc.)
 */
export function getPlainTextContent(content: string): string {
  if (!content) return '';
  
  let plain = content;
  plain = plain.replace(MARKDOWN_PATTERNS.bold, '$1');
  plain = plain.replace(MARKDOWN_PATTERNS.italic, '$1');
  
  return plain;
}
